import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { IClaimPayeeAccountSettings } from 'app/claims/models/claim-payee-account-settings.interface';
import { FileUploadChipStatus } from 'app/shared/components/file-upload-chip/enum/file-upload-chip-status.enum';
import { Carriers } from 'app/shared/enum/general-enum';
import { disallowDelimiters } from 'app/shared/form-validators/disallow-delimiters';
import { nameSanityValidator } from 'app/shared/form-validators/name-sanity';
import { ICountry } from 'app/shared/models/country.interface';
import { Location } from 'app/shared/models/location/location.model';
import { CountryService } from 'app/shared/services/country/country.service';
import { FormService } from 'app/shared/services/form/form.service';
import { USStateValidatorService } from 'app/shared/services/us-state-validator/us-state-validator.service';
import { ZipCodeService } from 'app/shared/services/zip-code/zip-code.service';
import { TrackingStatus } from 'app/track-shipments/models/tracking-status.enum';
import { APP_CONFIG } from 'config/app.config';
import { debounceTime } from 'rxjs/operators';
import { IClaimAdditionalInformation } from '../../../models/claim-additional-information.interface';
import { IClaimContact } from '../../../models/claim-contact.interface';
import { IClaimDetails } from '../../../models/claim-details.interface';
import { ClaimDocumentType } from '../../../models/claim-document-type.enum';
import { IClaimDocumentUpload } from '../../../models/claim-document-upload.interface';
import { IClaimMerchandise } from '../../../models/claim-merchandise.interface';
import { ClaimPackageIssue } from '../../../models/claim-package-issue.enum';
import { ClaimPackageLocation } from '../../../models/claim-package-location.enum';
import { IClaimPackaging } from '../../../models/claim-packaging.interface';
import { ClaimPaymentMethod } from '../../../models/claim-payment-method.enum';
import { ClaimRecipientContacted } from '../../../models/claim-recipient-contacted.enum';
import { ClaimStatus } from '../../../models/claim-status.enum';
import { ClaimType } from '../../../models/claim-type.enum';
import { IClaim } from '../../../models/claim.interface';
import { ClaimService } from '../../../services/claim.service';
import { FILE_CLAIM_CONFIG } from '../config/file-claim.config';
import { IClaimAdditionalInformationForm } from '../models/forms/claim-additional-information-form.interface';
import { IClaimContactForm } from '../models/forms/claim-contact-form.interface';
import { IClaimDetailsForm } from '../models/forms/claim-details-form.interface';
import { IClaimDocumentMetaForm } from '../models/forms/claim-document-meta-form.interface';
import { IClaimDocumentUploadForm } from '../models/forms/claim-document-upload-form.interface';
import { IClaimForm } from '../models/forms/claim-form.interface';
import { IClaimMerchandiseForm } from '../models/forms/claim-merchandise-form.interface';
import { IClaimPackagingForm } from '../models/forms/claim-packaging-form.interface';
import { IClaimPayeeAccountSettingsForm } from '../models/forms/claim-payee-account-settings-form.interface';
import { IClaimPayeeInformationForm } from '../models/forms/claim-payee-information-form.interface';
import { IClaimShipmentForm } from '../models/forms/claim-shipment-form.interface';
import { BehaviorSubject } from 'rxjs';


@Injectable()
export class ClaimFormService {
    public claimForm: FormGroup<IClaimForm>;
    public shipFromCountries: ICountry[] = [];
    public shipToCountries: ICountry[] = [];

    public constructor(private readonly fb: FormBuilder,
                       private readonly claimService: ClaimService,
                       private readonly translateService: TranslateService,
                       private readonly countryService: CountryService,
                       private readonly zipCodeService: ZipCodeService,
                       private readonly formService: FormService,
                       private readonly usStateValidatorService: USStateValidatorService,
    ) {
        this.initForm();
    }

    private _countries: ICountry[] = [];

    public set countries(value: ICountry[]) {
        this._countries = value;
    }

    public isCashWalletEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public cashWalletStatus$: BehaviorSubject<number> = new BehaviorSubject<number>(0);  //  0 - No action yet, 1 - Accepted, 2 - Rejected
    
    /**
     * Adds a new merchandise FormGroup to the FormArray.
     */
    public addMerchandise(merchandise?: IClaimMerchandise): void {
        if (this.claimForm.controls.merchandises.length >= FILE_CLAIM_CONFIG.claimMerchandise.maxQuantity) {
            return;
        }

        const merchandises = this.claimForm.controls.merchandises;
        const merchandiseForm = this.createMerchandiseForm();
        merchandiseForm.controls.claimedAmount.valueChanges.subscribe(() => {
            this.claimForm.controls.merchandises.updateValueAndValidity();
        });

        if (merchandise) {
            merchandiseForm.patchValue({ ...merchandise });
        }

        merchandises.push(merchandiseForm);
    }

    public duplicateMerchandise(index: number): void {
        if (this.claimForm.controls.merchandises.length >= FILE_CLAIM_CONFIG.claimMerchandise.maxQuantity) {
            return;
        }

        const merchandises = this.claimForm.controls.merchandises;

        const isInvalidIndex = index < 0 || index >= merchandises.length;
        if (isInvalidIndex) {
            return;
        }

        const merchandise = merchandises.at(index) as FormGroup<IClaimMerchandiseForm>;
        this.addMerchandise(merchandise.value);
    }

    /**
     * Removes a merchandise FormGroup at the given index.
     * @param index - The index of the merchandise to remove.
     */
    public removeMerchandise(index: number): void {
        if (this.claimForm.controls.merchandises.length <= 0) {
            return;
        }

        const merchandises = this.claimForm.controls.merchandises;

        const isInvalidIndex = index < 0 || index >= merchandises.length;
        if (isInvalidIndex) {
            return;
        }

        merchandises.removeAt(index);
    }

    public populateForm(claim: IClaim): void {
        this.claimForm.patchValue({
            claimId: claim.claimId,
            claimNumber: claim.claimNumber,
            // trackingNumber: claim.shipment?.trackingNumber,
            status: claim.status,
            customerId: claim.customerId,
            claimantContact: claim.claimantContact,
            claimsContactEmail: claim.claimsContactEmail,
            setClaimsContactEmailDefault: claim.setClaimsContactEmailDefault,
            shipment: claim.shipment,
            claimDetails: claim.claimDetails,
            packaging: claim.packaging,
            additionalInformation: claim.additionalInformation,
            payeeInformation: claim.payeeInformation,
        });

        this.claimForm.controls.merchandises.clear();
        for (const merchandise of claim.merchandises) {
            this.addMerchandise(merchandise);
        }

        if (!this.claimForm.controls.merchandises.length) {
            this.addMerchandise();
        }

        this.claimForm.controls.documentUploads.clear();
        const documentFormGroups = claim.documentUploads.map(document => this.createDocumentUploadFormFromDocument(document));
        for (const documentFormGroup of documentFormGroups) {
            this.claimForm.controls.documentUploads.push(documentFormGroup);
        }

        this.claimForm.controls.shipment.markAllAsTouched();

        // const errors = this.formService.getFormErrorsWithMessages(this.claimForm);
        // if (errors) {
        //     console.log(errors);
        // }
    }

    // public convertUploadedFilesToBase64(): Observable<IClaimFileUploadBase64> {
    //     return this.formService.convertFileFormToBase64<IClaimFileUploadBase64>(this.claimForm.controls.fileUpload);
    // }

    public resetClaimForm(): void {
        this.claimForm.reset({
            shipment: {
                currencyCode: 'USD',
            },
        });

        this.claimForm.controls.documentUploads.clear();

        this.claimForm.controls.merchandises.clear();
        this.claimForm.controls.merchandises.push(this.createMerchandiseForm());
        for (const merchandise of this.claimForm.controls.merchandises.controls) {
            merchandise.patchValue({
                invoiceValueCurrency: this.claimService.getClaimCurrency(),
                claimedAmountCurrency: this.claimService.getClaimCurrency(),
            });
        }

        this.claimForm.updateValueAndValidity();
        this.claimForm.markAsPristine();
        this.claimForm.markAsUntouched();
        this.claimService.getUserContacts().subscribe((res) => {
            this.updateContacts(res.accountPayableContact, res.mailingContact);
        });
    }

    public createDocumentUploadForm(file: File, documentType: ClaimDocumentType): FormGroup<IClaimDocumentUploadForm> {
        if (!file || !documentType) {
            return;
        }

        return this.fb.group<IClaimDocumentUploadForm>({
            file: new FormControl<File | null>(file),
            documentId: new FormControl<string | null>(null),
            fileName: new FormControl<string | null>(file.name),
            mimeType: new FormControl<string | null>(file.type),
            documentType: new FormControl<ClaimDocumentType | null>(documentType),
            isPendingDelete: new FormControl<boolean>(false),
            status: new FormControl<FileUploadChipStatus>(FileUploadChipStatus.PENDING_UPLOAD),
            message: new FormControl<string | null>(null),
        });
    }

    public createDocumentUploadFormFromDocument(document: IClaimDocumentUpload): FormGroup<IClaimDocumentUploadForm> {
        if (!document) {
            return;
        }

        return this.fb.group<IClaimDocumentUploadForm>({
            file: new FormControl<File | null>(document.file),
            documentId: new FormControl<string | null>(document.documentId),
            fileName: new FormControl<string | null>(document.fileName),
            mimeType: new FormControl<string | null>(document.mimeType),
            documentType: new FormControl<ClaimDocumentType | null>(document.documentType),
            isPendingDelete: new FormControl<boolean>(document.status === FileUploadChipStatus.PENDING_DELETE),
            status: new FormControl<FileUploadChipStatus>(document.status || FileUploadChipStatus.NONE),
            message: new FormControl<string | null>(document.message),
        });
    }

    public removeDocumentMeta(formArray: FormArray<FormGroup<IClaimDocumentMetaForm>>, index: number): void {
        if (formArray.length <= 0) {
            return;
        }

        const isInvalidIndex = index < 0 || index >= formArray.length;
        if (isInvalidIndex) {
            return;
        }

        formArray.removeAt(index);
    }

    public updateStateValidators(formGroup: FormGroup<IClaimContactForm>, country: ICountry): void {
        if (!country) {
            return;
        }

        const stateControl = formGroup.controls.state;
        stateControl.clearValidators();
        stateControl.updateValueAndValidity({ emitEvent: false });

        stateControl.setValidators([
            // PP-340: Include CA as country to require state/province code
            this.formService.requiredValidatorIf(country.IsStateAware || ['CA'].includes(country?.CountryCode)),
            RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxStateLength }),
            this.usStateValidatorService.usStateValidator<IClaimContact>(x => x.countryCode === 'US'),
            disallowDelimiters(),
            nameSanityValidator(),
        ]);
        stateControl.markAsTouched();
        stateControl.updateValueAndValidity();
    }

    public updateZipValidators(formGroup: FormGroup<IClaimContactForm>, country: ICountry): void {
        if (!country) {
            return;
        }

        const zipControl = formGroup.controls.zip;
        zipControl.clearValidators();
        zipControl.updateValueAndValidity({ emitEvent: false });

        zipControl.setValidators([
            this.formService.requiredValidatorIf(country.IsPostalCodeAware),
            country?.PostalCodePattern && RxwebValidators.pattern({ expression: { zipCode: this.zipCodeService.patternToRegex(country?.PostalCodePattern) } }),
            // RxwebValidators.maxLength({ value: country?.PostalCodeDigits || FILE_CLAIM_CONFIG.claimContact.maxZipLength }),
            disallowDelimiters(),
        ].filter(Boolean));
        zipControl.markAsTouched();
        zipControl.updateValueAndValidity();
    }

    public isStateAware(countryCode: string): boolean {
        if (!countryCode || !this._countries?.length) {
            return false;
        }

        const country = this._countries?.find(x => x.CountryCode === countryCode);
        if (!country) {
            return false;
        }

        return country.IsStateAware;
    }

    public isZipAware(countryCode: string): boolean {
        if (!countryCode || !this._countries?.length) {
            return false;
        }

        const country = this._countries?.find(x => x.CountryCode === countryCode);
        if (!country) {
            return false;
        }

        return country.IsPostalCodeAware;
    }

    private validateTotalClaimedAmount(maxAmount: number): ValidatorFn {
        return (formArray: FormArray<FormGroup<IClaimMerchandiseForm>>): ValidationErrors | null => {
            if (!(formArray instanceof FormArray)) {
                return null;
            }

            const totalClaimedAmount = formArray.controls.reduce((sum, group) => {
                const claimedAmount = Number(group.controls.claimedAmount?.value ?? 0);

                return +sum + claimedAmount;
            }, 0);

            if (totalClaimedAmount > maxAmount) {
                return {
                    totalClaimedAmountExceeded: {
                        actual: totalClaimedAmount,
                        max: maxAmount,
                    },
                };
            }

            return null;
        };
    }

    private updateContacts(accountPayableContact: Location, mailingContact: Location): void {
        if (accountPayableContact) {
            const payeeAccountSettingsForm = this.claimForm.controls.payeeInformation.controls.payeeAccountSettings;
            payeeAccountSettingsForm.patchValue({
                companyName: accountPayableContact?.CompanyName || mailingContact?.CompanyName,
                firstName: accountPayableContact?.FirstName || mailingContact?.FirstName,
                lastName: accountPayableContact?.LastName || mailingContact?.LastName,
            }, { emitEvent: false });

            const mailingAddressForm = this.claimForm.controls.payeeInformation.controls.mailingAddress;
            mailingAddressForm.patchValue({
                companyName: accountPayableContact?.CompanyName || mailingContact?.CompanyName,
                firstName: accountPayableContact?.FirstName || mailingContact?.FirstName,
                lastName: accountPayableContact?.LastName || mailingContact?.LastName,
            }, { emitEvent: false });
        }

        if (mailingContact) {
            const mailingAddressForm = this.claimForm.controls.payeeInformation.controls.mailingAddress;
            mailingAddressForm.patchValue({
                address1: mailingContact.StreetAddress,
                address2: mailingContact.ApartmentSuite,
                city: mailingContact.City,
                state: mailingContact.State,
                zip: mailingContact.Zip,
                countryCode: mailingContact.Country,
                phone: mailingContact.TelephoneNo,
                email: mailingContact.Email,
            }, { emitEvent: false });
        }
    }

    private initForm(): void {
        this.claimForm = this.fb.group<IClaimForm>({
            claimId: new FormControl<number | null>(null),
            claimNumber: new FormControl<string | null>(null),
            // trackingNumber: new FormControl<string | null>(null),
            status: new FormControl<ClaimStatus | null>(null),
            customerId: new FormControl<string | null>(null),
            claimantContact: this.createContactForm(false),
            claimsContactEmail: new FormControl<string | null>(null, [
                RxwebValidators.required(),
                RxwebValidators.email(),
                RxwebValidators.maxLength({ value: 60 }),
            ]),
            setClaimsContactEmailDefault: new FormControl<boolean>(false),
            shipment: this.fb.group<IClaimShipmentForm>({
                trackingNumber: new FormControl<string | null>(null),
                carrier: new FormControl<Carriers | null>(null),
                shipDate: new FormControl<string | null>(null),
                trackingStatus: new FormControl<TrackingStatus | null>(null),
                deliveredTo: new FormControl<string | null>(null),
                insuredValue: new FormControl<number | null>(null),
                inputType: new FormControl<string | null>(null),
                currencyCode: new FormControl<string | null>('USD'),
                shipperContact: this.createContactForm(false),
                receiverContact: this.createContactForm(false),
            }),
            claimDetails: this.fb.group<IClaimDetailsForm>({
                claimType: new FormControl<ClaimType | null>(null, [RxwebValidators.required()]),
                claimPackageIssue: new FormControl<ClaimPackageIssue | null>(null, [
                    RxwebValidators.required({
                        conditionalExpression: (x: IClaimDetails) => x.claimType === ClaimType.LOST,
                    }),
                ]),
            }),
            merchandises: new FormArray<FormGroup<IClaimMerchandiseForm>>([], [
                RxwebValidators.required(),
                RxwebValidators.minLength({ value: FILE_CLAIM_CONFIG.claimMerchandise.minQuantity }),
                RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimMerchandise.maxQuantity }),
                this.validateTotalClaimedAmount(FILE_CLAIM_CONFIG.claimMerchandise.maxClaimedAmount),
            ]),
            packaging: this.fb.group<IClaimPackagingForm>({
                hasOuterBox: new FormControl<boolean | null>(null, [RxwebValidators.required()]),
                outerBoxDescription: new FormControl<string | null>(null, [
                    RxwebValidators.required({
                        conditionalExpression: (x: IClaimPackaging) => x.hasOuterBox === true,
                    }),
                    this.guidewireSupportedCharactersValidator(),
                ]),
                hasInnerBox: new FormControl<boolean | null>(null, [RxwebValidators.required()]),
                innerBoxDescription: new FormControl<string | null>(null, [
                    RxwebValidators.required({
                        conditionalExpression: (x: IClaimPackaging) => x.hasInnerBox === true,
                    }),
                    this.guidewireSupportedCharactersValidator(),
                ]),
                hasOtherPackaging: new FormControl<boolean | null>(null, [RxwebValidators.required()]),
                otherPackagingDescription: new FormControl<string | null>(null, [
                    RxwebValidators.required({
                        conditionalExpression: (x: IClaimPackaging) => x.hasOtherPackaging === true,
                    }),
                    this.guidewireSupportedCharactersValidator(),
                ]),
            }),
            additionalInformation: this.fb.group<IClaimAdditionalInformationForm>({
                recipientContacted: new FormControl<ClaimRecipientContacted | null>(null, [
                    RxwebValidators.required({
                        conditionalExpression: (x: IClaimAdditionalInformation) => {
                            return [
                                ClaimType.LOST,
                            ].includes(this.claimForm?.controls.claimDetails.controls.claimType.value);
                        },
                    }),
                ]),
                packageLocation: new FormControl<ClaimPackageLocation | null>(null, [
                    RxwebValidators.required({
                        conditionalExpression: (x: IClaimAdditionalInformation) => {
                            return [
                                ClaimType.MISSING_CONTENTS,
                                ClaimType.DAMAGED,
                            ].includes(this.claimForm?.controls.claimDetails.controls.claimType.value);
                        },
                    }),
                ]),
                details: new FormControl<string | null>(null, [
                    RxwebValidators.required(),
                    this.guidewireSupportedCharactersValidator(),
                ]),
            }),
            documentUploads: new FormArray<FormGroup<IClaimDocumentUploadForm>>([]),
            payeeInformation: this.fb.group<IClaimPayeeInformationForm>({
                paymentMethod: new FormControl<ClaimPaymentMethod | null>(null, [RxwebValidators.required()]),
                payeeAccountSettings: this.fb.group<IClaimPayeeAccountSettingsForm>({
                    companyName: new FormControl<string | null>(null),
                    firstName: new FormControl<string | null>(null, [RxwebValidators.required({ conditionalExpression: (x: IClaimPayeeAccountSettings) => !x.companyName })]),
                    lastName: new FormControl<string | null>(null, [RxwebValidators.required({ conditionalExpression: (x: IClaimPayeeAccountSettings) => !x.companyName })]),
                }),
                mailingAddress: this.fb.group<IClaimContactForm>({
                    companyName: new FormControl<string | null>(
                        this.claimForm?.controls.payeeInformation.controls.payeeAccountSettings.controls.companyName.value || null,
                        [
                            RxwebValidators.required({
                                conditionalExpression: (x: IClaimContact) => {
                                    return this.claimForm?.controls.payeeInformation.controls.paymentMethod.value === ClaimPaymentMethod.CHECK
                                        && !x.firstName
                                        && !x.lastName;
                                },
                            }),
                            RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxCompanyNameLength }),
                        ]),
                    firstName: new FormControl<string | null>(
                        this.claimForm?.controls.payeeInformation.controls.payeeAccountSettings.controls.firstName.value || null,
                        [
                            RxwebValidators.required({
                                conditionalExpression: (x: IClaimContact) => {
                                    return this.claimForm?.controls.payeeInformation.controls.paymentMethod.value === ClaimPaymentMethod.CHECK
                                        && !x.companyName;
                                },
                            }),
                            RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxFirstNameLength }),
                        ]),
                    lastName: new FormControl<string | null>(
                        this.claimForm?.controls.payeeInformation.controls.payeeAccountSettings.controls.lastName.value || null,
                        [
                            RxwebValidators.required({
                                conditionalExpression: (x: IClaimContact) => {
                                    return this.claimForm?.controls.payeeInformation.controls.paymentMethod.value === ClaimPaymentMethod.CHECK
                                        && !x.companyName;
                                },
                            }),
                            RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxLastNameLength }),
                        ]),
                    address1: new FormControl<string | null>(null, [
                        RxwebValidators.required({
                            conditionalExpression: this.mailingAddressConditionalRequired(),
                        }),
                        RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxAddress1Length }),
                    ]),
                    address2: new FormControl<string | null>(null, [RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxAddress2Length })]),
                    city: new FormControl<string | null>(null, [
                        RxwebValidators.required({
                            conditionalExpression: this.mailingAddressConditionalRequired(),
                        }),
                        RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxAddress2Length }),
                        disallowDelimiters(),
                        nameSanityValidator(),
                    ]),
                    state: new FormControl<string | null>(null, [
                        RxwebValidators.required({
                            conditionalExpression: (x: IClaimContact) => {
                                return this.claimForm?.controls.payeeInformation.controls.paymentMethod.value === ClaimPaymentMethod.CHECK
                                    && this.isStateAware(x.countryCode);
                            },
                        }),
                        RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxStateLength }),
                        this.usStateValidatorService.usStateValidator<IClaimContact>(x => x.countryCode === 'US'),
                        disallowDelimiters(),
                        nameSanityValidator(),
                    ]),
                    zip: new FormControl<string | null>(null, [
                        RxwebValidators.required({
                            conditionalExpression: (x: IClaimContact) => {
                                return this.claimForm?.controls.payeeInformation.controls.paymentMethod.value === ClaimPaymentMethod.CHECK
                                    && this.isZipAware(x.countryCode);
                            },
                        }),
                        RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxZipLength }),
                        disallowDelimiters(),
                    ]),
                    countryCode: new FormControl<string | null>(null, [
                        RxwebValidators.required({
                            conditionalExpression: this.mailingAddressConditionalRequired(),
                        }),
                        RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxCountryLength }),
                    ]),
                    phone: new FormControl<string | null>(null, [
                        RxwebValidators.required({
                            conditionalExpression: this.mailingAddressConditionalRequired(),
                        }),
                        RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxPhoneLength }),
                        RxwebValidators.pattern({ expression: { phone: APP_CONFIG.PHONE_REGEX } }),
                    ]),
                    email: new FormControl<string | null>(null, [
                        // RxwebValidators.required({
                        //     conditionalExpression: this.mailingAddressConditionalRequired(),
                        // }),
                        RxwebValidators.email(),
                        // RxwebValidators.pattern({ expression: { email: RegexPattern.EMAIL } }),
                        RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxEmailLength }),
                    ]),
                }),
            }),
            isTermsAgreed: new FormControl<boolean>(false, [RxwebValidators.requiredTrue()]),
        });

        this.claimForm.controls.claimDetails.controls.claimType.valueChanges
            .pipe(debounceTime(0)) // this fixes a form validation timing issue on form controls that depends on this value.
            .subscribe(value => {
                const packageLocationControl = this.claimForm.controls.additionalInformation.controls.packageLocation;
                const claimPackageIssueControl = this.claimForm.controls.claimDetails.controls.claimPackageIssue;
                const recipientContactedControl = this.claimForm.controls.additionalInformation.controls.recipientContacted;

                if (value === ClaimType.LOST) {
                    packageLocationControl.setValue(null, { emitEvent: false });
                } else {
                    claimPackageIssueControl.setValue(null, { emitEvent: false });
                    recipientContactedControl.setValue(null, { emitEvent: false });
                }

                packageLocationControl.updateValueAndValidity();
                packageLocationControl.markAsPristine();
                packageLocationControl.markAsUntouched();

                claimPackageIssueControl.updateValueAndValidity();
                claimPackageIssueControl.markAsPristine();
                claimPackageIssueControl.markAsUntouched();

                recipientContactedControl.updateValueAndValidity();
                recipientContactedControl.markAsPristine();
                recipientContactedControl.markAsUntouched();

                const documents = [...this.claimForm.controls.documentUploads.value];
                const uploadedDocuments = documents.filter(x => !!x.documentId);
                const pendingUploadDocuments =
                    documents.filter(x =>
                        x.status === FileUploadChipStatus.PENDING_UPLOAD
                        && FILE_CLAIM_CONFIG.claimFileUpload.claimDocumentMap[value].includes(x.documentType),
                    );
                this.claimForm.controls.documentUploads.clear();

                for (const document of uploadedDocuments) {
                    const shouldBeDeleted = !FILE_CLAIM_CONFIG.claimFileUpload.claimDocumentMap[value]?.includes(document.documentType);
                    if (shouldBeDeleted) {
                        document.status = FileUploadChipStatus.PENDING_DELETE;
                    }

                    this.claimForm.controls.documentUploads.push(this.createDocumentUploadFormFromDocument(document));
                }

                for (const document of pendingUploadDocuments) {
                    this.claimForm.controls.documentUploads.push(this.createDocumentUploadFormFromDocument(document));
                }

                this.claimForm.controls.documentUploads.updateValueAndValidity();
            });

        this.claimForm.controls.merchandises.valueChanges
            .pipe(debounceTime(300))
            .subscribe(() => {
                this.claimForm.controls.merchandises.updateValueAndValidity();
            });

        // Add an empty instance of merchandise. At least one merchandise is required.
        this.addMerchandise();

        this.claimForm.controls.packaging.controls.hasOuterBox.valueChanges
            .pipe(debounceTime(0))
            .subscribe(value => {
                const outerBoxDescriptionControl = this.claimForm.controls.packaging.controls.outerBoxDescription;
                if (!value) {
                    outerBoxDescriptionControl.setValue(null, { emitEvent: false });
                }

                outerBoxDescriptionControl.updateValueAndValidity();
                outerBoxDescriptionControl.markAsPristine();
                outerBoxDescriptionControl.markAsUntouched();
            });

        this.claimForm.controls.packaging.controls.hasInnerBox.valueChanges
            .pipe(debounceTime(0))
            .subscribe(value => {
                const innerBoxDescriptionControl = this.claimForm.controls.packaging.controls.innerBoxDescription;
                if (!value) {
                    innerBoxDescriptionControl.setValue(null, { emitEvent: false });
                }

                innerBoxDescriptionControl.updateValueAndValidity();
                innerBoxDescriptionControl.markAsPristine();
                innerBoxDescriptionControl.markAsUntouched();
            });

        this.claimForm.controls.packaging.controls.hasOtherPackaging.valueChanges
            .pipe(debounceTime(0))
            .subscribe(value => {
                const otherPackagingDescriptionControl = this.claimForm.controls.packaging.controls.otherPackagingDescription;
                if (!value) {
                    otherPackagingDescriptionControl.setValue(null, { emitEvent: false });
                }

                otherPackagingDescriptionControl.updateValueAndValidity();
                otherPackagingDescriptionControl.markAsPristine();
                otherPackagingDescriptionControl.markAsUntouched();
            });

        this.claimForm.controls.shipment.controls.shipperContact.controls.countryCode.valueChanges
            .pipe(debounceTime(0))
            .subscribe(countryCode => {
                    if (!countryCode || !this.shipFromCountries?.length) {
                        return;
                    }

                    const country = this.shipFromCountries?.find((country) => country.CountryCode === countryCode);
                    if (!country) {
                        return;
                    }

                    this.updateStateValidators(this.claimForm.controls.shipment.controls.shipperContact, country);
                    this.updateZipValidators(this.claimForm.controls.shipment.controls.shipperContact, country);
                },
            );

        this.claimForm.controls.shipment.controls.receiverContact.controls.countryCode.valueChanges
            .pipe(debounceTime(0))
            .subscribe(countryCode => {
                    if (!countryCode || !this.shipToCountries?.length) {
                        return;
                    }

                    const country = this.shipToCountries?.find((country) => country.CountryCode === countryCode);
                    if (!country) {
                        return;
                    }

                    this.updateStateValidators(this.claimForm.controls.shipment.controls.receiverContact, country);
                    this.updateZipValidators(this.claimForm.controls.shipment.controls.receiverContact, country);
                },
            );
    }

    private mailingAddressConditionalRequired(): (x: IClaimContact) => boolean {
        return (x: IClaimContact) => this.claimForm?.controls.payeeInformation.controls.paymentMethod.value === ClaimPaymentMethod.CHECK;
    }

    private createContactForm(isEmailRequired = true): FormGroup<IClaimContactForm> {
        return this.fb.group<IClaimContactForm>({
            companyName: new FormControl<string | null>(null, [RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxCompanyNameLength })]),
            firstName: new FormControl<string | null>(null, [
                RxwebValidators.required({ conditionalExpression: (x: IClaimContact) => !x.companyName }),
                RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxFirstNameLength }),
            ]),
            lastName: new FormControl<string | null>(null, [
                RxwebValidators.required({ conditionalExpression: (x: IClaimContact) => !x.companyName }),
                RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxLastNameLength }),
            ]),
            phone: new FormControl<string | null>(null, [
                RxwebValidators.required(),
                RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxPhoneLength }),
                RxwebValidators.pattern({ expression: { phone: APP_CONFIG.PHONE_REGEX } }),
            ]),
            email: new FormControl(null, [
                ...(isEmailRequired ? [RxwebValidators.required()] : []),
                RxwebValidators.email(),
                RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxEmailLength }),
            ]),
            address1: new FormControl<string | null>(null, [
                RxwebValidators.required(),
                RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxAddress1Length }),
            ]),
            address2: new FormControl<string | null>(null, [
                RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxAddress2Length }),
            ]),
            city: new FormControl<string | null>(null, [
                RxwebValidators.required(),
                RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxCityLength }),
                disallowDelimiters(),
                nameSanityValidator(),
            ]),
            state: new FormControl<string | null>(null, [
                RxwebValidators.required({
                    conditionalExpression: (x: IClaimContact) => this.isStateAware(x.countryCode || ''),
                }),
                RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxStateLength }),
                disallowDelimiters(),
                nameSanityValidator(),
            ]),
            zip: new FormControl<string | null>(null, [
                RxwebValidators.required({
                    conditionalExpression: (x: IClaimContact) => this.isZipAware(x.countryCode || ''),
                }),
                RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxZipLength }),
                disallowDelimiters(),
            ]),
            countryCode: new FormControl<string | null>(null, [
                RxwebValidators.required(),
                RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimContact.maxCountryLength }),
            ]),
        });
    }

    private createMerchandiseForm(): FormGroup<IClaimMerchandiseForm> {
        return this.fb.group<IClaimMerchandiseForm>({
            commodityTypeCode: new FormControl<string | null>(null, [RxwebValidators.required()]),
            description: new FormControl<string | null>(null, [
                RxwebValidators.required(),
                RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimMerchandise.maxDescriptionLength }),
                this.guidewireSupportedCharactersValidator(),
            ]),
            numberOfItems: new FormControl<number | null>(0, [
                RxwebValidators.required(),
                RxwebValidators.minNumber({ value: FILE_CLAIM_CONFIG.claimMerchandise.minNumberOfItems }),
            ]),
            invoiceValue: new FormControl<number | null>(0, [
                RxwebValidators.required(),
                RxwebValidators.minNumber({ value: FILE_CLAIM_CONFIG.claimMerchandise.minInvoiceValue }),
                RxwebValidators.maxNumber({ value: FILE_CLAIM_CONFIG.claimMerchandise.maxInvoiceValue }),
            ]),
            invoiceValueCurrency: new FormControl<string | null>(this.claimService.getClaimCurrency(), [RxwebValidators.required()]),
            claimedAmount: new FormControl<number | null>(0, [
                RxwebValidators.required(),
                RxwebValidators.minNumber({ value: FILE_CLAIM_CONFIG.claimMerchandise.minClaimedAmount }),
                RxwebValidators.maxNumber({ value: FILE_CLAIM_CONFIG.claimMerchandise.maxClaimedAmount }),
            ]),
            claimedAmountCurrency: new FormControl<string | null>(this.claimService.getClaimCurrency(), [RxwebValidators.required()]),
            itemNumber: new FormControl<string | null>(null, [
                RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimMerchandise.maxItemNumberLength }),
                this.guidewireSupportedCharactersValidator(),
            ]),
            serialNumber: new FormControl<string | null>(null, [
                RxwebValidators.maxLength({ value: FILE_CLAIM_CONFIG.claimMerchandise.maxSerialNumberLength }),
                this.guidewireSupportedCharactersValidator(),
            ]),
        });
    }

    private guidewireSupportedCharactersValidator(): ValidatorFn {
        return RxwebValidators.pattern({
            expression: {
                pattern: FILE_CLAIM_CONFIG.claimMerchandise.descriptionRegex,
            },
            message: this.translateService.instant('claims.fileClaim.form.merchandiseDescription.merchandise.descriptionErrorMessage'),
        });
    }
}
